/**
 * Traitement des photos d'évolution avant envoi.
 *
 * Trois problèmes, tous côté navigateur :
 *
 *   1. HEIC. Le format par défaut de l'iPhone. iOS le décode nativement, Chrome et
 *      Firefox non — et un `<img>` sur un HEIC échoue silencieusement (image vide, pas
 *      d'erreur). On détecte donc le cas et on renvoie un message explicite plutôt
 *      qu'une vignette blanche. Note : quand la photo est prise ou choisie via un
 *      `<input accept="image/*">`, iOS convertit lui-même en JPEG dans la plupart des
 *      cas ; le HEIC brut n'arrive que par certains partages de fichiers.
 *
 *   2. Orientation EXIF. Une photo prise en portrait arrive souvent en paysage avec un
 *      tag d'orientation. `createImageBitmap(blob, { imageOrientation: "from-image" })`
 *      applique la rotation et rend les pixels déjà redressés — ce qui compte, puisque
 *      la recompression via canvas PERD le tag EXIF : sans ça la photo repartirait
 *      couchée pour toujours.
 *
 *   3. Poids. Une photo d'iPhone fait 3 à 5 Mo. On redimensionne le côté long à 1400 px
 *      et on réencode : largement assez pour une comparaison avant/après, et le bucket
 *      plafonne à 8 Mo par fichier.
 */

export const MAX_EDGE_PX = 1400
const JPEG_QUALITY = 0.82

export class PhotoPipelineError extends Error {}

function looksHeic(file: File): boolean {
  const type = file.type.toLowerCase()
  if (type === "image/heic" || type === "image/heif") return true
  // Certains navigateurs ne renseignent aucun type sur un HEIC : on retombe sur le nom.
  return /\.(heic|heif)$/i.test(file.name)
}

/**
 * Décode en respectant l'orientation EXIF. `imageOrientation` est ignoré par les vieux
 * navigateurs, d'où le repli par `<img>` — qui applique lui aussi l'EXIF depuis Chrome 81
 * et Safari 13.4, ce qui couvre le parc réel de l'app (PWA installée).
 */
async function decodeOriented(file: File): Promise<ImageBitmap | HTMLImageElement> {
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" })
  } catch {
    // Repli
  }

  const url = URL.createObjectURL(file)
  try {
    const img = new Image()
    img.src = url
    await img.decode()
    if (!img.naturalWidth) throw new Error("largeur nulle")
    return img
  } catch {
    throw new PhotoPipelineError(
      looksHeic(file)
        ? "Ce format de photo (HEIC) n'est pas lisible par ton navigateur. Sur iPhone : Réglages → Appareil photo → Formats → « Le plus compatible », ou renvoie la photo en JPEG."
        : "Cette image n'a pas pu être lue. Essaie un JPEG ou un PNG."
    )
  } finally {
    // Révoqué après decode() : l'image est déjà en mémoire, garder l'URL fuiterait.
    URL.revokeObjectURL(url)
  }
}

export interface NormalizedPhoto {
  blob: Blob
  width: number
  height: number
}

/** Redresse, redimensionne et recompresse. Le résultat est prêt pour le bucket. */
export async function normalizePhoto(file: File): Promise<NormalizedPhoto> {
  if (!file.type.startsWith("image/") && !looksHeic(file)) {
    throw new PhotoPipelineError("Ce fichier n'est pas une image.")
  }

  const source = await decodeOriented(file)
  // ImageBitmap et HTMLImageElement exposent tous deux `width`, mais celui de l'élément
  // est la largeur de RENDU (0 tant qu'il n'est pas dans le document) : il faut naturalWidth.
  const srcW = source instanceof HTMLImageElement ? source.naturalWidth : source.width
  const srcH = source instanceof HTMLImageElement ? source.naturalHeight : source.height

  const scale = Math.min(1, MAX_EDGE_PX / Math.max(srcW, srcH))
  const width = Math.round(srcW * scale)
  const height = Math.round(srcH * scale)

  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new PhotoPipelineError("Traitement de l'image impossible sur cet appareil.")
  ctx.drawImage(source as CanvasImageSource, 0, 0, width, height)
  if (!(source instanceof HTMLImageElement)) source.close()

  const blob = await canvasToBlob(canvas, "image/jpeg", JPEG_QUALITY)
  return { blob, width, height }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new PhotoPipelineError("Encodage de l'image échoué."))),
      type,
      quality
    )
  })
}

// ── Image de comparaison avant / après ──────────────────────────────────────

export interface ComparisonDelta {
  label: string
  value: string
}

export interface ComparisonInput {
  beforeUrl: string
  afterUrl: string
  beforeCaption: string
  afterCaption: string
  title: string
  deltas: ComparisonDelta[]
}

const CMP_W = 1080
const CMP_H = 1350

/**
 * Compose une image unique à partir des deux photos et des écarts chiffrés.
 * Format 4:5, le plus sûr pour un partage Instagram/WhatsApp sans recadrage.
 *
 * Les deux photos sont chargées par URL SIGNÉE : elles proviennent du bucket privé et
 * l'URL expire. `crossOrigin` reste non renseigné volontairement — les URL signées de
 * Supabase servent avec les en-têtes CORS qui vont bien, et forcer "anonymous" ferait
 * échouer le chargement là où il marche aujourd'hui.
 */
export async function renderComparisonImage(input: ComparisonInput): Promise<Blob> {
  const [before, after] = await Promise.all([loadImage(input.beforeUrl), loadImage(input.afterUrl)])

  const canvas = document.createElement("canvas")
  canvas.width = CMP_W
  canvas.height = CMP_H
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new PhotoPipelineError("Génération de l'image impossible sur cet appareil.")

  ctx.fillStyle = "#060a13"
  ctx.fillRect(0, 0, CMP_W, CMP_H)

  // Lumière ambiante cyan, comme en haut du Profil.
  const glow = ctx.createRadialGradient(CMP_W / 2, 40, 0, CMP_W / 2, 40, 620)
  glow.addColorStop(0, "rgba(6,182,212,0.20)")
  glow.addColorStop(1, "rgba(6,182,212,0)")
  ctx.fillStyle = glow
  ctx.fillRect(0, 0, CMP_W, 700)

  ctx.fillStyle = "#f2f5f9"
  ctx.font = "700 46px -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif"
  ctx.textAlign = "center"
  ctx.fillText(input.title, CMP_W / 2, 84)

  const gap = 24
  const padX = 40
  const photoW = (CMP_W - padX * 2 - gap) / 2
  const photoH = 760
  const photoY = 124

  drawCover(ctx, before, padX, photoY, photoW, photoH, 24)
  drawCover(ctx, after, padX + photoW + gap, photoY, photoW, photoH, 24)

  ctx.font = "600 30px ui-monospace, SFMono-Regular, Menlo, monospace"
  ctx.fillStyle = "#98a2b3"
  ctx.fillText(input.beforeCaption, padX + photoW / 2, photoY + photoH + 48)
  ctx.fillStyle = "#67e8f9"
  ctx.fillText(input.afterCaption, padX + photoW + gap + photoW / 2, photoY + photoH + 48)

  // Écarts chiffrés, répartis sur une ligne.
  const deltas = input.deltas.slice(0, 3)
  if (deltas.length > 0) {
    const boxY = photoY + photoH + 84
    const boxH = 150
    const boxW = (CMP_W - padX * 2 - gap * (deltas.length - 1)) / deltas.length
    deltas.forEach((d, i) => {
      const x = padX + i * (boxW + gap)
      roundRect(ctx, x, boxY, boxW, boxH, 22)
      ctx.fillStyle = "rgba(13,20,32,0.92)"
      ctx.fill()
      ctx.strokeStyle = "rgba(30,41,59,0.9)"
      ctx.lineWidth = 2
      ctx.stroke()

      ctx.textAlign = "center"
      ctx.fillStyle = "#6b7280"
      ctx.font = "600 24px ui-monospace, SFMono-Regular, Menlo, monospace"
      ctx.fillText(d.label.toUpperCase(), x + boxW / 2, boxY + 52)
      ctx.fillStyle = "#f2f5f9"
      ctx.font = "700 52px -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif"
      ctx.fillText(d.value, x + boxW / 2, boxY + 112)
    })
  }

  ctx.textAlign = "center"
  ctx.fillStyle = "#475569"
  ctx.font = "600 24px ui-monospace, SFMono-Regular, Menlo, monospace"
  ctx.fillText("ZEYONE FIT", CMP_W / 2, CMP_H - 36)

  return canvasToBlob(canvas, "image/jpeg", 0.9)
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new PhotoPipelineError("Une des deux photos n'a pas pu être chargée."))
    img.src = url
  })
}

/** Dessine l'image en `cover` dans un cadre arrondi, sans la déformer. */
function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number, y: number, w: number, h: number, radius: number
) {
  ctx.save()
  roundRect(ctx, x, y, w, h, radius)
  ctx.clip()
  const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight)
  const dw = img.naturalWidth * scale
  const dh = img.naturalHeight * scale
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh)
  ctx.restore()
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}
